import { api, apiUrl, getSessionToken } from "../client";

/**
 * WF10 Mobile Capture — floor staff photograph paper production sheets; the
 * office reviews them minutes later. Backend: ubik-workflows scripts/capture_api.py.
 *
 * Uploads use raw fetch (multipart) — deliberately NOT CapacitorHttp in the
 * Android build: its FormData handling drops Content-Type boundaries on
 * device (open bugs v5–v8), and multipart image upload is this workflow's
 * entire job. Plain fetch + apiUrl() + nginx CORS is the wire contract.
 */

export type CaptureRole = "NONE" | "CAPTURE" | "REVIEW" | "ADMIN";
export type AppSurface = "full" | "capture";

export interface CaptureMe {
  enabled: boolean;
  role: CaptureRole;
  /** Which app this binary renders. Absent/undefined → "full" (back-compat). */
  app_surface?: AppSurface;
  can_capture: boolean;
  can_review: boolean;
  can_admin: boolean;
  access_floor: string;
  org_id: string | null;
  sheet_types: string[];
  facilities: string[];
  centers: string[];
  max_upload_bytes: number;
  max_pages_per_batch: number;
}

export interface CaptureBatch {
  id: string;
  facility: string;
  center: string | null;
  production_date: string;
  sheet_type: string;
  note: string | null;
  status: "OPEN" | "SUBMITTED";
  captured_by: string;
  device_captured_at: string | null;
  created_at: string;
  submitted_at: string | null;
  page_count: number | null;
}

export interface CapturePage {
  id: string;
  page_no: number;
  document_id: string;
  original_filename: string;
  content_type: string;
  byte_size: number;
  created_at: string;
}

export interface CaptureBatchDetail extends CaptureBatch {
  pages: CapturePage[];
}

export interface UploadedPage {
  id: string;
  page_no: number;
  document_id: string;
  sha256: string;
  byte_size: number;
  duplicate_bytes: boolean;
}

export interface DuplicateFlag {
  page_no: number;
  document_id: string;
  matches_document_id: string;
  matches_filename: string;
  distance: number;
}

export interface NewBatch {
  facility: string;
  center?: string | null;
  production_date: string; // YYYY-MM-DD
  sheet_type: string;
  note?: string | null;
  device_captured_at?: string | null;
}

export const captureApi = {
  me: (): Promise<CaptureMe> => api.get<CaptureMe>("/v1/capture/me"),

  createBatch: (payload: NewBatch): Promise<CaptureBatch> =>
    api.post<CaptureBatch>("/v1/capture/batches", payload),

  listBatches: (params: {
    status?: "OPEN" | "SUBMITTED";
    production_date?: string;
    facility?: string;
    mine?: boolean;
  } = {}): Promise<{ batches: CaptureBatch[] }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.production_date) qs.set("production_date", params.production_date);
    if (params.facility) qs.set("facility", params.facility);
    if (params.mine) qs.set("mine", "true");
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<{ batches: CaptureBatch[] }>(`/v1/capture/batches${suffix}`);
  },

  getBatch: (batchId: string): Promise<CaptureBatchDetail> =>
    api.get<CaptureBatchDetail>(`/v1/capture/batches/${batchId}`),

  /**
   * One page, independently retryable. The backend answers a retry of the
   * same bytes at the same slot with 200 + the existing row, so the offline
   * queue can re-send without double-reporting.
   */
  uploadPage: async (
    batchId: string,
    pageNo: number,
    file: Blob,
    filename: string,
  ): Promise<UploadedPage> => {
    const form = new FormData();
    form.append("page_no", String(pageNo));
    form.append("file", file, filename);
    const headers: Record<string, string> = {};
    const token = getSessionToken();
    if (token) headers["X-Session-Token"] = token;
    const res = await fetch(apiUrl(`/v1/capture/batches/${batchId}/pages`), {
      method: "POST",
      headers,
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(
        typeof body.detail === "string" ? body.detail : `Upload failed (HTTP ${res.status})`,
      );
    }
    return res.json() as Promise<UploadedPage>;
  },

  submitBatch: (batchId: string): Promise<{ batch: CaptureBatch; duplicates: DuplicateFlag[] }> =>
    api.post(`/v1/capture/batches/${batchId}/submit`, {}),

  /** Authenticated image bytes — auth rides in a header, so <img src> can't. */
  fetchDocumentBlob: async (documentId: string): Promise<Blob> => {
    const headers: Record<string, string> = {};
    const token = getSessionToken();
    if (token) headers["X-Session-Token"] = token;
    const res = await fetch(apiUrl(`/v1/capture/documents/${documentId}/content`), {
      headers,
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Document fetch failed (HTTP ${res.status})`);
    return res.blob();
  },

  orgMembers: (): Promise<CaptureMember[]> => api.get<CaptureMember[]>("/v1/capture/org-members"),

  setAccess: (userId: string, role: CaptureRole, appSurface?: AppSurface) =>
    api.put(`/v1/capture/access/${userId}`, { role, app_surface: appSurface }),

  revokeAccess: (userId: string) =>
    api.delete(`/v1/capture/access/${userId}`),
};

export interface CaptureMember {
  user_id: string;
  name?: string | null;
  /** Null for phone-only floor staff. Render with memberLabel(), never raw. */
  email: string | null;
  phone?: string | null;
  is_workspace_admin: boolean;
  role: CaptureRole;
  granted_role: CaptureRole | null;
  app_surface: AppSurface;
  has_access: boolean;
  granted_at: string | null;
}
